import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Tag, Plus, Loader2, Trash2, UploadCloud, Pencil, EyeOff, Eye } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/api";
import { fmtMoney } from "@/components/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function fileToDataUrl(file, maxPx = 1000, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) { const s = maxPx / Math.max(width, height); width = Math.round(width * s); height = Math.round(height * s); }
      const c = document.createElement("canvas"); c.width = width; c.height = height;
      const ctx = c.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, width, height); ctx.drawImage(img, 0, 0, width, height);
      resolve(c.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}

const BLANK = { name: "", description: "", price: "", stock: "", pack_size: 1, pack_unit: "stems", image_url: "", is_active: true, sell_price: "", price_point: "" };
const PRICE_POINTS = ["8.99", "11.99", "14.99", "19.99", "24.99"];

export default function Specials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // editing object or null
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => { setLoading(true); adminApi.listSpecials().then(setItems).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => setForm({ ...BLANK });
  const openEdit = (s) => setForm({ ...s, price: String(s.price), stock: String(s.stock), sell_price: s.sell_price ? String(s.sell_price) : "", price_point: s.price_point ? String(s.price_point) : "" });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    try { set({ image_url: await fileToDataUrl(file) }); } catch (err) { toast.error(err.message); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const price = parseFloat(form.price); const stock = parseInt(form.stock || "0", 10);
    if (!(price > 0)) return toast.error("Price must be greater than 0");
    setSaving(true);
    try {
      await adminApi.upsertSpecial({ id: form.id, name: form.name.trim(), description: form.description || "", price, stock: isNaN(stock) ? 0 : stock, pack_size: Number(form.pack_size) || 1, pack_unit: form.pack_unit || "", image_url: form.image_url || "", is_active: form.is_active !== false, sell_price: parseFloat(form.sell_price) || 0, price_point: form.price_point ? parseFloat(form.price_point) : null });
      toast.success(form.id ? "Special updated" : "Special created");
      setForm(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const toggleActive = async (s) => {
    try { await adminApi.upsertSpecial({ ...s, price: s.price, stock: s.stock, is_active: !s.is_active }); load(); toast.success(s.is_active ? "Special expired" : "Special reactivated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? This cannot be undone.`)) return;
    try { await adminApi.deleteSpecial(s.id); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="admin-specials">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#28282E]">Friday Specials</h1>
          <p className="text-sm text-[#28282E]/70">Warehouse extras with limited stock. Active specials appear on the store home "Friday Specials" tile.</p>
        </div>
        <Button onClick={openNew} data-testid="special-new-btn" className="rounded-full bg-[#D46117] text-white hover:bg-[#b8500f]"><Plus className="mr-2 h-4 w-4" /> New special</Button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20 text-[#28282E]/55"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-[#28282E]/55">No specials yet. Create one to fill the Friday Specials tile.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((s) => (
            <div key={s.id} data-testid={`special-card-${s.id}`} className={`overflow-hidden rounded-2xl border border-slate-200 bg-white ${!s.is_active ? "opacity-60" : ""}`}>
              <div className="relative h-36 bg-slate-100">
                {s.image_url ? <img src={s.image_url} alt={s.name} className="h-full w-full object-cover" /> : null}
                {!s.is_active && <span className="absolute left-2 top-2 rounded-full bg-[#28282E] px-2 py-0.5 text-[10px] font-bold uppercase text-white">Expired</span>}
                <span className="absolute right-2 top-2 rounded-full bg-[#EBBAD1] px-2 py-0.5 text-[10px] font-bold uppercase text-[#28282E]">{s.stock} in stock</span>
              </div>
              <div className="p-4">
                <div className="font-serif text-lg font-bold text-[#28282E]">{s.name}</div>
                <div className="text-sm font-bold text-[#D46117]">{fmtMoney(s.price)} <span className="text-xs font-normal text-[#28282E]/55">/ {s.unit || "carton"}</span></div>
                {s.description ? <p className="mt-1 line-clamp-2 text-xs text-[#28282E]/70">{s.description}</p> : null}
                <div className="mt-3 flex gap-2">
                  <button data-testid={`special-edit-${s.id}`} onClick={() => openEdit(s)} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-[#EBBAD1] py-1.5 text-xs font-semibold text-[#28282E] hover:opacity-90"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                  <button data-testid={`special-toggle-${s.id}`} onClick={() => toggleActive(s)} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-slate-100 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200">{s.is_active ? <><EyeOff className="h-3.5 w-3.5" /> Expire</> : <><Eye className="h-3.5 w-3.5" /> Restore</>}</button>
                  <button data-testid={`special-delete-${s.id}`} onClick={() => remove(s)} className="grid h-8 w-8 place-items-center rounded-lg text-[#28282E]/55 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-w-lg" data-testid="special-dialog">
          {form && (<>
            <DialogHeader><DialogTitle className="font-serif">{form.id ? "Edit special" : "New special"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="mb-1 block text-xs font-semibold text-[#28282E]/70">Name</Label><Input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="special-name" /></div>
              <div><Label className="mb-1 block text-xs font-semibold text-[#28282E]/70">Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })} data-testid="special-desc" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="mb-1 block text-xs font-semibold text-[#28282E]/70">Price ($)</Label><Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set({ price: e.target.value })} data-testid="special-price" /></div>
                <div><Label className="mb-1 block text-xs font-semibold text-[#28282E]/70">Stock</Label><Input type="number" min="0" value={form.stock} onChange={(e) => set({ stock: e.target.value })} data-testid="special-stock" /></div>
                <div><Label className="mb-1 block text-xs font-semibold text-[#28282E]/70">Sell price ($)</Label><Input type="number" min="0" step="0.01" value={form.sell_price} onChange={(e) => set({ sell_price: e.target.value, price_point: PRICE_POINTS.includes(e.target.value) ? e.target.value : form.price_point })} data-testid="special-sell-price" /></div>
                <div><Label className="mb-1 block text-xs font-semibold text-[#28282E]/70">Production report column (price point)</Label>
                  <select data-testid="special-price-point" value={form.price_point} onChange={(e) => set({ price_point: e.target.value })} className="block w-full rounded-md border border-slate-200 px-2 py-2 text-sm">
                    <option value="">— not mapped —</option>{PRICE_POINTS.map((p) => <option key={p} value={p}>SEASONAL BUNCH {p}</option>)}
                  </select></div>
                <div><Label className="mb-1 block text-xs font-semibold text-[#28282E]/70">Pack size</Label><Input type="number" min="1" value={form.pack_size} onChange={(e) => set({ pack_size: e.target.value })} data-testid="special-pack" /></div>
              </div>
              <div>
                <Label className="mb-1 block text-xs font-semibold text-[#28282E]/70">Image</Label>
                <div className="flex gap-2">
                  <Input value={form.image_url} onChange={(e) => set({ image_url: e.target.value })} placeholder="https://… or upload" className="text-xs" data-testid="special-image-url" />
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="special-upload"><UploadCloud className="h-4 w-4" /></Button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
                </div>
                {form.image_url ? <img src={form.image_url} alt="" className="mt-2 h-28 w-full rounded-xl object-cover" /> : null}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="special-save" className="bg-[#D46117] text-white hover:bg-[#b8500f]">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
